import React, { useContext, Fragment, useEffect, useState, useRef } from "react";
import MapContext from "../MapContext";
import { useSelector, useDispatch } from "react-redux";
import {
    BiShapePolygon,
    BiEdit,
    BiEraser,
    BiRuler,
    BiTrash,
    BiAlbum,
} from "react-icons/bi";
import { Button, ButtonGroup, Toolbar, ToolbarItem } from "@progress/kendo-react-buttons";
import "./Measure.css";
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from "ol/style.js";
import { Draw, Modify } from "ol/interaction.js";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Snapping } from "../Markup/Snapping";
import { setSnappingReady } from "../../redux/setConfigSlice";
import { getMapLayerById } from "../utils/mapUtils";
import {
    setMeasureAreaUnits,
    setMeasureDistanceUnits,
    setBearingToggle,
} from "../../redux/toolSelectSlice";
import { drawStyleFunction } from "./measureUtils";
import { DialogActionsBar, Dialog } from "@progress/kendo-react-dialogs";
import { Switch } from "@progress/kendo-react-inputs";
import { MeasureUnitsState } from "./measureUtils";

export const Measure = () => {
    const { map } = useContext(MapContext);
    const dispatch = useDispatch();
    const [measureTool, setMeasureTool] = useState();
    const modify = useRef();
    const draw = useRef();

    const bearingToggle = useSelector((state) => state.toolSelect.bearingToggle);
    const bearingRef = useRef(bearingToggle);

    const measureToolRef = useRef();
    const [snapping, setSnapping] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);

    const areaRef = useRef();
    const distanceRef = useRef();
    const areaUnit = useSelector((state) => state.toolSelect.measureAreaUnits);
    const distanceUnit = useSelector((state) => state.toolSelect.measureDistanceUnits);
    const configUnits = useSelector((state) => state.configData.mapConfig.map.units);
    areaRef.current = areaUnit;
    distanceRef.current = distanceUnit;

    /** @type {MeasureUnitsState} */
    const unitArgs = {
        configUnits,
        areaUnits: areaRef.current,
        distanceUnits: distanceRef.current,
        bearing: bearingRef.current,
    };

    /**
     * This function dispatches an action to update the distance measurement unit.
     *
     * @param {Object} e - The event object. It should have a 'target' property with
     * a 'value' property that contains the new distance unit.
     */
    const distanceUnitChange = (e) => {
        dispatch(setMeasureDistanceUnits(e.target.value));
    };

    /**
     * This function dispatches an action to update the area measurement unit.
     *
     * @param {Object} e - The event object. It should have a 'target' property
     * with a 'value' property that contains the new area unit.
     */
    const areaUnitChange = (e) => {
        dispatch(setMeasureAreaUnits(e.target.value));
    };

    /**
     * This function dispatches an action to update the bearing toggle state
     * and updates the bearingRef current value.
     *
     * @param {Object} e - The event object. It should have a 'value' property
     * that contains the new bearing toggle state.
     */
    const handleBearingToggle = (e) => {
        dispatch(setBearingToggle(e.value));
        bearingRef.current = e.value;
    };

    // Set default measure tool
    useEffect(() => {
        setMeasureTool("LineString");
    }, []);

    /**
     * This function handles the erase click event.
     *
     * If the current measure tool is "Erase", it gets the "drawLayer" and "coordinateLayer" from the map,
     * finds the features at the click location, and removes them from their respective layers.
     *
     * @param {Object} e - The event object. It should have an 'originalEvent' property that contains the native event.
     */
    const handleEraseClick = async (e) => {
        if (measureToolRef.current !== "Erase") return;
        const markupLayer = getMapLayerById(map, "drawLayer");
        const coordinateLayer = getMapLayerById(map, "coordinateLayer");
        const pixel = await map.getEventPixel(e.originalEvent);
        const feature = await markupLayer.getFeatures(pixel);
        const coordFeature = await coordinateLayer.getFeatures(pixel);
        coordinateLayer.getSource().removeFeature(coordFeature[0]);
        markupLayer.getSource().removeFeature(feature[0]);
    };

    /**
     * This function adds a new interaction to the map.
     *
     * It creates a new Draw interaction with the specified tool, source, and style,
     * and adds it to the map.
     *
     * @param {string} tool - The type of the tool to use for the Draw interaction.
     * This determines the type of features that can be drawn.
     * @param {ol/source/Vector} markupSource - The source where the drawn features
     * will be stored.
     */
    const addInteraction = (tool, markupSource) => {
        const drawType = tool;
        draw.current = new Draw({
            source: markupSource,
            type: drawType,
            style: (feature) => {
                return drawStyleFunction(feature, drawStyle, unitArgs);
            },
        });
        map.addInteraction(draw.current);
    };

    /**
     * This function updates the style of a feature.
     *
     * It sets the Id of the feature to a string that includes the feature's
     * OpenLayers unique identifier (ol_uid), and sets the style of the feature
     * using the drawStyleFunction.
     *
     * @param {Object} e - The event object. It should have a 'feature'
     * property that contains the feature to update.
     */
    const updateFeatureStyle = (e) => {
        const uid = e.feature.ol_uid;
        e.feature.setId(`measure-feature-${uid}`);
        e.feature.setStyle((feature) => {
            return drawStyleFunction(feature, vectorStyle, unitArgs);
        });
    };

    // Add interaction, source and layers
    // runs once on component mount
    useEffect(() => {
        const markupSource = getMapLayerById(map, "drawLayer").getSource();
        modify.current = new Modify({ source: markupSource, style: modifyStyle });
        map.addInteraction(modify.current);
        modify.current.setActive(false);
        return () => {
            map.removeInteraction(modify.current);
            map.removeInteraction(draw.current);
        };
    }, []);

    // Runs on mount and everytime measureTool and bearingToggle changes
    useEffect(() => {
        // Update ref so app knows which measure tool is active
        measureToolRef.current = measureTool;
        if (measureTool === undefined) return;
        modify.current.setActive(false);
        if (measureTool === "Modify") {
            modify.current.setActive(true);
        } else if (measureTool === "Polygon") {
            const markupSource = getMapLayerById(map, "drawLayer").getSource();
            addInteraction(measureTool, markupSource);
            dispatch(setSnappingReady(true));
        } else if (measureTool === "LineString") {
            const markupSource = getMapLayerById(map, "drawLayer").getSource();
            addInteraction(measureTool, markupSource);
            dispatch(setSnappingReady(true));
        } else if (measureTool === "Circle") {
            const markupSource = getMapLayerById(map, "drawLayer").getSource();
            addInteraction(measureTool, markupSource);
            dispatch(setSnappingReady(true));
        } else if (measureTool === "Erase") {
            //erase shape
        } else if (measureTool === "Delete") {
            const markupSource = getMapLayerById(map, "drawLayer").getSource();
            const coordinateSource = getMapLayerById(map, "coordinateLayer").getSource();
            modify.current.setActive(false);
            coordinateSource.clear();
            markupSource.clear();
        }
        return () => {
            map.removeInteraction(draw.current);
        };
    }, [measureTool, bearingToggle]);

    //Add listenser for erase
    useEffect(() => {
        if (!map) return;
        map.on("click", handleEraseClick);
        return () => {
            map.un("click", handleEraseClick);
        };
    }, [map]);

    // update measurement units, update refs
    useEffect(() => {
        const markupSource = getMapLayerById(map, "drawLayer").getSource();
        markupSource.getFeatures().forEach((feat) => {
            if (feat.getId()?.includes("measure-feature")) {
                feat.setStyle((feature) => {
                    return drawStyleFunction(feature, vectorStyle, unitArgs);
                });
            }
        });
        markupSource.changed();
        markupSource.on("addfeature", updateFeatureStyle);
        return () => {
            markupSource.un("addfeature", updateFeatureStyle);
        };
    }, [distanceUnit, areaUnit, bearingToggle]);

    return (
        <Fragment>
            <h3 className="title">Measure</h3>
            <div className="measure-option-item">
                <h4>Tools</h4>
                <div className="measure-options">
                    <Toolbar className="measure-toolbar">
                        <ToolbarItem>
                            <ButtonGroup>
                                <Button
                                    togglable
                                    selected={measureTool === "Polygon"}
                                    onClick={() => setMeasureTool("Polygon")}>
                                    <BiShapePolygon />
                                    Area
                                </Button>
                                <Button
                                    togglable
                                    selected={measureTool === "LineString"}
                                    onClick={() => setMeasureTool("LineString")}>
                                    <BiRuler />
                                    Distance
                                </Button>
                                <Button
                                    togglable
                                    selected={measureTool === "Circle"}
                                    onClick={() => setMeasureTool("Circle")}>
                                    <BiAlbum />
                                    Circle
                                </Button>

                                <Button
                                    togglable
                                    selected={measureTool === "Modify"}
                                    onClick={() => setMeasureTool("Modify")}>
                                    <BiEdit />
                                    Edit
                                </Button>
                                <Button
                                    togglable
                                    selected={measureTool === "Erase"}
                                    onClick={() => setMeasureTool("Erase")}>
                                    <BiEraser />
                                    Erase
                                </Button>
                                <Button onClick={() => setDialogVisible(true)}>
                                    <BiTrash />
                                    Delete
                                </Button>
                            </ButtonGroup>
                            {dialogVisible && (
                                <Dialog
                                    title={"Delete all drawings"}
                                    onClose={() => setDialogVisible(false)}
                                    id="measure-delete-dialog">
                                    <p>Continue?</p>
                                    <DialogActionsBar>
                                        <Button onClick={() => setDialogVisible(false)}>
                                            No
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setDialogVisible(false);
                                                setMeasureTool("Delete");
                                            }}>
                                            Yes
                                        </Button>
                                    </DialogActionsBar>
                                </Dialog>
                            )}
                        </ToolbarItem>
                    </Toolbar>
                </div>
            </div>
            <div className="bearing-toggle measure-option-item">
                <h4>View Bearing Measurements</h4>
                <Switch
                    onChange={handleBearingToggle}
                    checked={bearingToggle}
                    thumbRounded={"full"}
                    trackRounded={"full"}
                />
            </div>
            <div className="measure-option-item">
                <h4>Units</h4>
                <div className="dropdowns">
                    <div>
                        <div>Distance Unit</div>
                        <DropDownList
                            style={{ width: "120px" }}
                            data={distanceUnits}
                            value={distanceUnit}
                            onChange={distanceUnitChange}
                        />
                    </div>
                    <div>
                        <div>Area Unit</div>
                        <DropDownList
                            style={{ width: "120px" }}
                            data={areaUnits}
                            value={areaUnit}
                            onChange={areaUnitChange}
                        />
                    </div>
                </div>
            </div>
            <div className="measure-option-item">
                <Snapping setSnapping={setSnapping} snapping={snapping} />
            </div>
        </Fragment>
    );
};

export default Measure;

const distanceUnits = [
    "Feet",
    "Miles",
    "Meters",
    "Yards",
    "Kilometers",
    "Nautical Miles",
];

const areaUnits = [
    "Feet \xB2",
    "Acres",
    "Miles \xB2",
    "Meters \xB2",
    "Yards \xB2",
    "Kilometers \xB2",
    "Nautical Miles \xB2",
    "Hectares",
];

const drawStyle = new Style({
    zIndex: 1000,
    fill: new Fill({
        color: "rgba(255, 255, 255, 0.2)",
    }),
    stroke: new Stroke({
        color: "rgba(0, 0, 0, 0.5)",
        width: 1.5,
    }),
    image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
            color: "rgba(0, 0, 0, 0.7)",
        }),
        fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
        }),
    }),
});

const vectorStyle = new Style({
    zIndex: 1000,
    fill: new Fill({
        color: "rgba(0, 0, 255, 0.1)",
    }),
    stroke: new Stroke({
        color: "rgba(0, 0, 255, 0.7)",
        width: 2,
    }),
    image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
            color: "rgba(0, 0, 255, 0.7)",
        }),
        fill: new Fill({
            color: "rgba(0, 0, 255, 0.2)",
        }),
    }),
});

const modifyStyle = new Style({
    image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
            color: "rgba(0, 0, 0, 0.7)",
        }),
        fill: new Fill({
            color: "rgba(0, 0, 0, 0.4)",
        }),
    }),
    text: new Text({
        text: "Drag to modify",
        font: "12px Calibri,sans-serif",
        fill: new Fill({
            color: "rgba(255, 255, 255, 1)",
        }),
        backgroundFill: new Fill({
            color: "rgba(0, 0, 0, 0.7)",
        }),
        padding: [2, 2, 2, 2],
        offsetX: 45,
        offsetY: -15,
    }),
});
